/**
 * SmallMap Component
 *
 * A small, static map display for showing a location marker.
 * Lazy loads the Leaflet library to avoid initialization issues.
 */

import React, {useEffect, useRef, useState} from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {IconButton} from '@mui/material';
import {Fullscreen, FullscreenExit} from '@mui/icons-material';

// Fix for default markers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix default icon issue with webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

interface SmallMapProps {
    latitude: number;
    longitude: number;
    width?: number;
    height?: number;
    locationName?: string;
    zoom?: number;
}

const SmallMap: React.FC<SmallMapProps> = ({
    latitude,
    longitude,
    width,
    height,
    locationName,
    zoom = 13
}) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        // Create map with pan and zoom enabled
        const map = L.map(mapRef.current, {
            center: [latitude, longitude],
            zoom: zoom,
            zoomControl: true,
            attributionControl: false,
            dragging: true,
            doubleClickZoom: true,
            scrollWheelZoom: true,
            boxZoom: true,
            keyboard: true,
            touchZoom: true
        });

        // Add tile layer
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
        }).addTo(map);

        // Add marker with optional tooltip
        const marker = L.marker([latitude, longitude]).addTo(map);
        if (locationName) {
            marker.bindTooltip(locationName, {
                permanent: false,
                direction: 'top',
                offset: L.point(0, -20)
            });
        }

        mapInstanceRef.current = map;

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []); // Only run once on mount

    // Update map view when coordinates or location name change
    useEffect(() => {
        if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([latitude, longitude], zoom);
            // Clear existing markers
            mapInstanceRef.current.eachLayer((layer) => {
                if (layer instanceof L.Marker) {
                    layer.remove();
                }
            });
            // Add new marker with optional tooltip
            const marker = L.marker([latitude, longitude]).addTo(mapInstanceRef.current);
            if (locationName) {
                marker.bindTooltip(locationName, {
                    permanent: false,
                    direction: 'top',
                    offset: L.point(0, -20)
                });
            }
        }
    }, [latitude, longitude, locationName, zoom]);

    const handleFullscreen = (): void => {
        if (!mapContainerRef.current) return;

        if (!isFullscreen) {
            // Enter fullscreen
            if (mapContainerRef.current.requestFullscreen) {
                mapContainerRef.current.requestFullscreen();
            } else if ((mapContainerRef.current as any).webkitRequestFullscreen) {
                (mapContainerRef.current as any).webkitRequestFullscreen();
            } else if ((mapContainerRef.current as any).msRequestFullscreen) {
                (mapContainerRef.current as any).msRequestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
                (document as any).webkitExitFullscreen();
            } else if ((document as any).msExitFullscreen) {
                (document as any).msExitFullscreen();
            }
            setIsFullscreen(false);
        }

        // Force map to recalculate its size after fullscreen change
        setTimeout(() => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.invalidateSize();
            }
        }, 300);
    };

    // Listen for fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = (): void => {
            setIsFullscreen(!!document.fullscreenElement);
            // Force map to recalculate its size
            if (mapInstanceRef.current) {
                setTimeout(() => {
                    mapInstanceRef.current?.invalidateSize();
                }, 100);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    return (
        <div
            ref={mapContainerRef}
            style={{
                position: 'relative',
                height: height ? `${height}px` : '100%',
                width: width ? `${width}px` : '100%'
            }}
        >
            <div
                ref={mapRef}
                style={{
                    height: '100%',
                    width: '100%'
                }}
            />
            <IconButton
                size="small"
                onClick={handleFullscreen}
                sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    '&:hover': {
                        backgroundColor: '#f4f4f4'
                    }
                }}
            >
                {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
            </IconButton>
        </div>
    );
};

export default SmallMap;